import { useState, type FormEvent } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { Container } from "@/shared/components/layout/Container";
import { Section } from "@/shared/components/layout/Section";
import { Button } from "@/shared/components/ui/Button";
import { PasswordFormField } from "@/shared/components/form/PasswordFormField";
import { useToast } from "@/shared/components/ui/Toast";
import { useResetPassword } from "@/shared/lib/auth";

export function PasswordReset() {
  const { token = "" } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const reset = useResetPassword();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const confirmMismatch = confirmPassword.length > 0 && confirmPassword !== password;

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (password !== confirmPassword) return;
    reset.mutate(
      { token, newPassword: password },
      {
        onSuccess: () => {
          toast({
            title: "Password updated",
            description: "Sign in with your new password.",
          });
          navigate("/auth/login");
        },
      },
    );
  }

  return (
    <Section spacing="lg">
      <Container size="sm">
        <div className="glass-surface-strong rounded-2xl p-8 flex flex-col gap-6">
          <div className="flex flex-col gap-2 text-center">
            <h1 className="type-heading-md text-text">Choose a new password</h1>
            <p className="type-body-base text-text-muted">
              Enter a new password for your account.
            </p>
          </div>
          <form className="flex flex-col gap-6" onSubmit={handleSubmit}>
            <PasswordFormField
              label="New password"
              name="password"
              autoComplete="new-password"
              required
              minLength={8}
              hint="At least 8 characters."
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <PasswordFormField
              label="Confirm password"
              name="confirmPassword"
              autoComplete="new-password"
              required
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              error={confirmMismatch ? "Passwords don't match." : undefined}
            />
            {reset.error ? (
              <p className="type-body-sm text-danger-strong mx-4">
                {reset.error.message}
              </p>
            ) : null}
            <Button type="submit" disabled={reset.isPending}>
              {reset.isPending ? "Updating…" : "Update password"}
            </Button>
          </form>
          <p className="type-body-sm text-text-muted text-center">
            <Link to="/auth/login" className="text-brand underline-offset-4 hover:underline">
              Back to sign in
            </Link>
          </p>
        </div>
      </Container>
    </Section>
  );
}
